import tokenManager from "@/tokenManager";

const AUTH_TERMINATION_CODE = "AUTH_SESSION_TERMINATED";
let terminationStarted = false;

const safeCommit = (store, mutation, payload) => {
  if (!store) return;

  try {
    store.commit(mutation, payload);
  } catch (_) {
    // Session cleanup must continue even if a store module is unavailable.
  }
};

const clearApplicationCaches = () => {
  if (typeof window === "undefined" || !("caches" in window)) return;

  window.caches
    .keys()
    .then((cacheNames) => Promise.all(cacheNames.map((name) => window.caches.delete(name))))
    .catch(() => {});
};

export const markAuthenticationTermination = (error) => {
  if (error && typeof error === "object") {
    error.code = AUTH_TERMINATION_CODE;
    error.isAuthenticationTermination = true;
  }

  return error;
};

export const isAuthenticationTermination = (error) =>
  error?.code === AUTH_TERMINATION_CODE || error?.isAuthenticationTermination === true;

export const clearClientSession = ({ store, preserveKeys = ["darkMode", "language"] } = {}) => {
  const preservedValues = {};

  try {
    preserveKeys.forEach((key) => {
      const value = window.localStorage.getItem(key);
      if (value !== null) preservedValues[key] = value;
    });
  } catch (_) {
    // Storage can be unavailable in restricted/private browser contexts.
  }

  try {
    tokenManager.clearToken();
  } catch (_) {
    // Continue clearing the remaining session data.
  }

  try {
    const keysToRemove = [];
    for (const key of Object.keys(window.localStorage)) {
      if (!preserveKeys.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key));

    Object.entries(preservedValues).forEach(([key, value]) => {
      window.localStorage.setItem(key, value);
    });
  } catch (_) {
    // Continue with session and in-memory cleanup.
  }

  try {
    window.sessionStorage.clear();
  } catch (_) {
    // Continue with in-memory cleanup.
  }

  safeCommit(store, "setAccessToken", "");
  safeCommit(store, "setLoggedUser", {});
  safeCommit(store, "setIsLoggedIn", false);
  safeCommit(store, "clearAuthorization");
  safeCommit(store, "setRolePermissions", []);
  safeCommit(store, "setPermissions", []);
  safeCommit(store, "setRoles", []);
  safeCommit(store, "setRole", {});
  safeCommit(store, "setSelectedRole", {});
  safeCommit(store, "setToast", {
    display: false,
    type: null,
    title: null,
    message: null,
  });

  clearApplicationCaches();
};

export const forceLoginRedirect = ({ store } = {}) => {
  if (terminationStarted) return false;

  terminationStarted = true;
  clearClientSession({ store, preserveKeys: ["darkMode", "language"] });

  if (typeof window !== "undefined") {
    const loginUrl = `${process.env.BASE_URL || "/"}login`.replace(/\/+/g, "/");
    window.location.replace(loginUrl);
  }

  return true;
};

export const hasSessionTerminationStarted = () => terminationStarted;
